import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Card, Tag, Modal, Form, Input, Select, DatePicker, message, InputNumber, Space, Divider, Typography, Row, Col, Tooltip, Empty, Descriptions, Grid } from 'antd';
import { PlusOutlined, CheckCircleOutlined, DollarOutlined, EyeOutlined, PrinterOutlined, EditOutlined, CloseOutlined } from '@ant-design/icons';
import ExportButton from '../components/ExportButton';
import client from '../api/client';
import dayjs from 'dayjs';
import SalesOrderPrintModal from '../components/SalesOrderPrintModal';
import { useAuth } from '../context/AuthContext';

const { Option } = Select;
const { Title } = Typography;

const SalesPage = () => {
    const { hasPermission, user } = useAuth();
    const isManagerOrAdmin = user?.role === 'ADMIN' || user?.role === 'MANAGER';
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
    const [isPrintModalVisible, setIsPrintModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewOrder, setViewOrder] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [editingOrder, setEditingOrder] = useState(null);
    const [form] = Form.useForm();
    const [paymentForm] = Form.useForm();
    const [bulkPaymentForm] = Form.useForm();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
    const [bulkConfirming, setBulkConfirming] = useState(false);
    const [bulkPaymentOpen, setBulkPaymentOpen] = useState(false);
    const [bulkPaying, setBulkPaying] = useState(false);
    const [bulkPaymentRows, setBulkPaymentRows] = useState([]);

    // Master data
    const [branches, setBranches] = useState([]);
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedItemCategoryId, setSelectedItemCategoryId] = useState(null);
    const [palayLots, setPalayLots] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('ALL');

    // Customer dropdown
    const [customers, setCustomers] = useState([]);
    const [isAddCustomerModalVisible, setIsAddCustomerModalVisible] = useState(false);
    const [addCustomerForm] = Form.useForm();

    const displayedOrderItems = useMemo(() => {
        const orderItems = viewOrder?.items || [];
        if (!viewOrder?.rice_allowance_batch) return orderItems;

        const grouped = new Map();
        orderItems.forEach((line) => {
            const key = [line.item_id, line.palay_lot_id || '', Number(line.unit_price || 0).toFixed(2)].join('|');
            const existing = grouped.get(key);
            if (existing) {
                existing.quantity += Number(line.quantity || 0);
                existing.line_total += Number(line.line_total || 0);
                return;
            }
            grouped.set(key, {
                ...line,
                id: `allowance-summary-${key}`,
                quantity: Number(line.quantity || 0),
                line_total: Number(line.line_total || 0),
            });
        });

        return Array.from(grouped.values());
    }, [viewOrder]);

    useEffect(() => {
        fetchOrders();
        fetchMasterData();
    }, []);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const { data } = await client.get('/transactions/sales');
            setOrders(data);
        } catch (error) {
            message.error('Failed to fetch sales orders');
        } finally {
            setLoading(false);
        }
    };

    const fetchMasterData = async () => {
        try {
            const [branchesRes, itemsRes, categoriesRes, lotsRes, customersRes] = await Promise.all([
                client.get('/master/branches'),
                client.get('/master/items'),
                client.get('/master/categories'),
                client.get('/inventory/palay-lots'),
                client.get('/customers', { params: { limit: 500, active: 'true' } }),
            ]);
            setBranches(branchesRes.data);
            setItems(itemsRes.data.filter(item => item.is_active));
            setCategories(categoriesRes.data);
            setPalayLots(lotsRes.data || []);
            setCustomers(customersRes.data.data || []);
        } catch (error) {
            console.error('Failed to fetch master data', error);
        }
    };

    const buildSalesOrderPayload = (values) => ({
        ...values,
        discount: values.discount ?? 0,
        order_date: values.order_date.format('YYYY-MM-DD HH:mm:ss'),
    });

    const openCreateModal = () => {
        setEditingOrder(null);
        setSelectedItemCategoryId(null);
        form.resetFields();
        form.setFieldsValue({
            order_number: `SO-${dayjs().valueOf()}`,
            order_date: dayjs(),
            branch_id: 1,
            discount: 0,
            items: [],
        });
        setIsModalVisible(true);
    };

    const closeSalesOrderModal = () => {
        setIsModalVisible(false);
        setEditingOrder(null);
        setSelectedItemCategoryId(null);
        form.resetFields();
    };

    const handleSaveSalesOrder = async (values) => {
        try {
            const payload = buildSalesOrderPayload(values);

            if (editingOrder) {
                await client.put(`/transactions/sales/${editingOrder.id}`, payload);
                message.success('Sales order updated successfully');
            } else {
                await client.post('/transactions/sales', payload);
                message.success('Sales order created successfully');
            }

            closeSalesOrderModal();
            fetchOrders();
        } catch (error) {
            message.error(error.response?.data?.message || `Failed to ${editingOrder ? 'update' : 'create'} sales order`);
        }
    };

    const handleConfirm = async (id) => {
        try {
            await client.post(`/transactions/sales/${id}/confirm`);
            message.success('Order confirmed and invoice generated');
            fetchOrders();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to confirm order';
            message.error(errorMessage);
        }
    };

    const handlePayment = async (values) => {
        const invoiceId = selectedOrder?.invoice?.id;
        if (!invoiceId) {
            message.error('Order has no invoice generated yet.');
            return;
        }

        try {
            await client.post('/transactions/payments', {
                ...values,
                invoice_id: invoiceId,
                payment_date: values.payment_date.format('YYYY-MM-DD HH:mm:ss'),
            });
            message.success('Payment recorded successfully');
            setIsPaymentModalVisible(false);
            paymentForm.resetFields();
            fetchOrders();
        } catch (error) {
            const validationErrors = error.response?.data?.errors;
            if (validationErrors) {
                paymentForm.setFields(
                    Object.entries(validationErrors).map(([name, errors]) => ({ name, errors }))
                );
            }
            message.error(error.response?.data?.message || 'Failed to record payment');
        }
    };

    const handleAddCustomer = async (values) => {
        try {
            const code = `CUST-${Date.now()}`;
            const res = await client.post('/customers', { ...values, code });
            message.success('Customer added successfully');

            // Reload customer list
            const customersRes = await client.get('/customers', { params: { limit: 500, active: 'true' } });
            setCustomers(customersRes.data.data || []);

            // Auto-select the new customer in the sales form
            form.setFieldValue('partner_id', res.data.id);

            setIsAddCustomerModalVisible(false);
            addCustomerForm.resetFields();
        } catch (error) {
            const validationErrors = error.response?.data?.errors;
            if (validationErrors) {
                addCustomerForm.setFields(
                    Object.entries(validationErrors).map(([name, errors]) => ({ name, errors }))
                );
            }
            message.error(error.response?.data?.message || 'Failed to add customer');
        }
    };

    const openPaymentModal = (order) => {
        setSelectedOrder(order);
        if (!order.invoice) {
            message.warning('Order has no invoice generated yet.');
            return;
        }
        paymentForm.setFieldsValue({
            invoice_number: order.invoice.invoice_number,
            payment_number: `PAY-${dayjs().valueOf()}`,
            amount: order.invoice.balance,
            payment_date: dayjs()
        });
        setIsPaymentModalVisible(true);
    };

    const openBulkConfirmModal = () => {
        if (!confirmableSelected.length) {
            message.warning('Select at least one draft order to confirm.');
            return;
        }
        setBulkConfirmOpen(true);
    };

    const handleBulkConfirm = async () => {
        const orderIds = confirmableSelected.map(order => order.id);
        if (!orderIds.length) return;

        setBulkConfirming(true);
        try {
            const { data } = await client.post('/transactions/sales/bulk-confirm', {
                sales_order_ids: orderIds,
            });
            message.success(data.message || `${orderIds.length} sales orders confirmed`);
            setBulkConfirmOpen(false);
            setSelectedRowKeys(keys => keys.filter(key => !orderIds.includes(Number(key))));
            await fetchOrders();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to confirm selected orders');
        } finally {
            setBulkConfirming(false);
        }
    };

    const openBulkPaymentModal = () => {
        if (!payableSelected.length) {
            message.warning('Select at least one confirmed invoice with an outstanding balance.');
            return;
        }

        setBulkPaymentRows(payableSelected.map(order => ({
            order_id: order.id,
            order_number: order.order_number,
            customer: order.customer?.name || order.customer_name || 'Walk-in',
            invoice_id: order.invoice.id,
            invoice_number: order.invoice.invoice_number,
            outstanding: Number(order.invoice.balance || 0),
            amount: Number(order.invoice.balance || 0),
        })));
        bulkPaymentForm.resetFields();
        bulkPaymentForm.setFieldsValue({
            payment_date: dayjs(),
            payment_method: 'CASH',
            reference_number: '',
        });
        setBulkPaymentOpen(true);
    };

    const updateBulkPaymentAmount = (invoiceId, amount) => {
        setBulkPaymentRows(rows => rows.map(row => (
            row.invoice_id === invoiceId ? { ...row, amount } : row
        )));
    };

    const removeBulkPaymentRow = (invoiceId) => {
        setBulkPaymentRows(rows => rows.filter(row => row.invoice_id !== invoiceId));
    };

    const handleBulkPayment = async () => {
        let values;
        try {
            values = await bulkPaymentForm.validateFields();
        } catch {
            return;
        }

        if (!bulkPaymentRows.length) {
            message.warning('Keep at least one invoice in the payment list.');
            return;
        }

        const invalidRow = bulkPaymentRows.find(row => (
            !Number.isFinite(Number(row.amount)) || Number(row.amount) <= 0 || Number(row.amount) > row.outstanding
        ));
        if (invalidRow) {
            message.error(`Enter an amount from ₱0.01 to ₱${invalidRow.outstanding.toLocaleString(undefined, { minimumFractionDigits: 2 })} for ${invalidRow.invoice_number}.`);
            return;
        }

        setBulkPaying(true);
        try {
            const { data } = await client.post('/transactions/payments/bulk', {
                payment_date: values.payment_date.format('YYYY-MM-DD HH:mm:ss'),
                payment_method: values.payment_method,
                reference_number: values.reference_number?.trim() || null,
                payments: bulkPaymentRows.map(row => ({
                    invoice_id: row.invoice_id,
                    amount: Number(row.amount),
                })),
            });
            const paidOrderIds = bulkPaymentRows.map(row => row.order_id);
            message.success(data.message || `${bulkPaymentRows.length} payments recorded`);
            setBulkPaymentOpen(false);
            setBulkPaymentRows([]);
            setSelectedRowKeys(keys => keys.filter(key => !paidOrderIds.includes(Number(key))));
            await fetchOrders();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to record selected payments');
        } finally {
            setBulkPaying(false);
        }
    };

    const handlePrintOrder = async (order) => {
        try {
            message.loading({ content: 'Loading sales order...', key: 'sales-print' });
            const { data } = await client.get(`/transactions/sales/${order.id}`);
            message.success({ content: 'Sales order loaded', key: 'sales-print', duration: 1 });
            setSelectedOrder(data);
            setIsPrintModalVisible(true);
        } catch (error) {
            message.error({ content: 'Failed to load sales order', key: 'sales-print' });
        }
    };

    const handleViewOrder = async (order) => {
        setViewLoading(true);
        setIsViewModalVisible(true);
        try {
            const { data } = await client.get(`/transactions/sales/${order.id}`);
            setViewOrder(data);
        } catch (error) {
            message.error('Failed to load sales order details');
        } finally {
            setViewLoading(false);
        }
    };

    const handleEditOrder = async (order) => {
        if (order.status !== 'DRAFT') {
            message.warning('Only draft sales orders can be edited.');
            return;
        }

        try {
            message.loading({ content: 'Loading sales order...', key: 'sales-edit' });
            const { data } = await client.get(`/transactions/sales/${order.id}`);

            if (data.status !== 'DRAFT') {
                message.warning({ content: 'Only draft sales orders can be edited.', key: 'sales-edit' });
                return;
            }

            setEditingOrder(data);
            setSelectedItemCategoryId(null);
            form.resetFields();
            form.setFieldsValue({
                order_number: data.order_number,
                reference_number: data.reference_number,
                ar_number: data.ar_number,
                order_date: dayjs(data.order_date),
                partner_id: data.partner_id,
                branch_id: data.branch_id,
                discount: data.discount ?? 0,
                remarks: data.remarks,
                items: (data.items || []).map((item) => ({
                    item_id: item.item_id,
                    palay_lot_id: item.palay_lot_id,
                    quantity: Number(item.quantity),
                    unit_price: Number(item.unit_price),
                })),
            });
            setIsModalVisible(true);
            message.success({ content: 'Sales order loaded', key: 'sales-edit', duration: 1 });
        } catch (error) {
            message.error({ content: 'Failed to load sales order', key: 'sales-edit' });
        }
    };

    const columns = [
        // Responsive columns with tooltips and improved icons
        { title: 'Order #', dataIndex: 'order_number', key: 'order_number', responsive: ['xs', 'sm', 'md'] },
        { title: 'Reference #', dataIndex: 'reference_number', key: 'reference_number', render: (val) => val || <span style={{ color: '#bbb' }}>—</span> },
        { title: 'Order Date', dataIndex: 'order_date', key: 'order_date', responsive: ['xs', 'sm', 'md'], render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm') },
        {
            title: 'Encoded', dataIndex: 'created_at', key: 'created_at',
            render: (val, record) => val ? (
                <Tooltip title={`Encoded by ${record.creator?.name || 'Unknown'}`}>
                    <span style={{ whiteSpace: 'nowrap' }}>{dayjs(val).format('YYYY-MM-DD HH:mm')}</span>
                </Tooltip>
            ) : <span style={{ color: '#bbb' }}>—</span>
        },
        {
            title: 'Confirmed', dataIndex: 'confirmed_at', key: 'confirmed_at',
            render: (val, record) => val ? (
                <Tooltip title={`Confirmed by ${record.confirmer?.name || 'Unknown'}`}>
                    <span style={{ whiteSpace: 'nowrap' }}>{dayjs(val).format('YYYY-MM-DD HH:mm')}</span>
                </Tooltip>
            ) : <span style={{ color: '#bbb' }}>—</span>
        },
        { title: 'Customer', dataIndex: ['customer', 'name'], key: 'customer' },
        { title: 'Branch', dataIndex: ['branch', 'name'], key: 'branch' },
        {
            title: 'Total', dataIndex: 'total_amount', key: 'total_amount', responsive: ['sm', 'md'], render: (val) => (
                <Tooltip title={Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}>
                    ₱{Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Tooltip>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const colors = { DRAFT: 'default', CONFIRMED: 'blue', COMPLETED: 'green', CANCELLED: 'red' };
                return <Tag color={colors[status]}>{status}</Tag>;
            }
        },
        {
            title: 'Payment',
            dataIndex: 'payment_status',
            key: 'payment_status',
            render: (status) => {
                const colors = { UNPAID: 'red', PARTIAL: 'orange', PAID: 'green' };
                return <Tag color={colors[status]}>{status}</Tag>;
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size={[8, 8]} wrap>
                    <Button
                        icon={<EyeOutlined />}
                        onClick={() => handleViewOrder(record)}
                    >
                        View
                    </Button>
                    {record.status === 'DRAFT' && (
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => handleEditOrder(record)}
                        >
                            Edit
                        </Button>
                    )}
                    {record.status === 'DRAFT' && hasPermission('post_transaction') && (
                        <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            onClick={() => handleConfirm(record.id)}
                        >
                            Confirm
                        </Button>
                    )}
                    {record.status === 'CONFIRMED' && record.payment_status !== 'PAID' && isManagerOrAdmin && (
                        <Button
                            icon={<DollarOutlined />}
                            onClick={() => openPaymentModal(record)}
                        >
                            Pay
                        </Button>
                    )}
                    {record.payment_status === 'PAID' && (
                        <Tooltip title="Print Sales Order">
                            <Button icon={<PrinterOutlined />} onClick={() => handlePrintOrder(record)} />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    const StatCard = ({ title, value, prefix, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: parseFloat(value) < 0 ? '#ef4444' : 'var(--text-primary)' }}>
                        {prefix}{parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    const filteredOrders = orders.filter((o) => {
        const term = searchTerm.trim().toLowerCase();
        const matchesSearch = !term || (
            (o.order_number || '').toLowerCase().includes(term) ||
            (o.reference_number || '').toLowerCase().includes(term) ||
            (o.customer?.name || o.customer_name || '').toLowerCase().includes(term) ||
            (o.branch?.name || '').toLowerCase().includes(term) ||
            (o.status || '').toLowerCase().includes(term) ||
            (o.payment_status || 'UNPAID').toLowerCase().includes(term)
        );
        const matchesPaymentStatus = paymentStatusFilter === 'ALL'
            || (o.payment_status || 'UNPAID') === paymentStatusFilter;

        return matchesSearch && matchesPaymentStatus;
    });

    const canConfirmOrders = hasPermission('post_transaction');
    const isPayableOrder = (order) => (
        order.status === 'CONFIRMED'
        && order.payment_status !== 'PAID'
        && order.invoice
        && ['ISSUED', 'OVERDUE'].includes(order.invoice.status)
        && Number(order.invoice.balance || 0) > 0
    );
    const selectedOrders = orders.filter(order => selectedRowKeys.some(key => Number(key) === Number(order.id)));
    const confirmableSelected = canConfirmOrders
        ? selectedOrders.filter(order => order.status === 'DRAFT')
        : [];
    const payableSelected = isManagerOrAdmin
        ? selectedOrders.filter(isPayableOrder)
        : [];
    const bulkPaymentTotal = bulkPaymentRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const bulkOutstandingTotal = bulkPaymentRows.reduce((sum, row) => sum + row.outstanding, 0);
    const canSelectOrder = (order) => (
        (canConfirmOrders && order.status === 'DRAFT')
        || (isManagerOrAdmin && isPayableOrder(order))
    );

    const confirmedOrders = filteredOrders.filter(o => o.status === 'CONFIRMED' || o.status === 'COMPLETED');
    const totalSales = confirmedOrders.reduce((sum, o) => sum + parseFloat(o.total_amount), 0);
    const totalAr = confirmedOrders.filter(o => o.payment_status !== 'PAID').reduce((sum, o) => sum + parseFloat(o.invoice?.balance || 0), 0);
    const pendingCount = filteredOrders.filter(o => o.status === 'DRAFT').length;
    const unpaidInvoices = filteredOrders.filter(o => o.payment_status === 'UNPAID' && o.status !== 'DRAFT').length;
    const resolveItem = (itemId) => items.find((item) => item.id === itemId);
    const isPalayItem = (itemId) => {
        const item = resolveItem(itemId);
        const slug = item?.category?.slug?.toLowerCase?.() || '';
        const name = item?.category?.name?.toLowerCase?.() || '';
        return slug === 'palay' || name === 'palay';
    };

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-color)' }}>Rice Sales & AR</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Manage customer orders, invoices, and accounts receivable.</Typography.Text>
                </div>
                <Space wrap>
                    <ExportButton
                        filename="sales-orders"
                        sheetName="Sales Orders"
                        title="Rice Sales & AR"
                        headers={['Order #', 'Reference #', 'Date', 'Customer', 'Branch', 'Item', 'Qty', 'Unit', 'Unit Price', 'Line Total', 'Total Amount', 'Status', 'Payment Status', 'Encoded At', 'Encoded By', 'Confirmed At', 'Confirmed By']}
                        getData={() => filteredOrders.flatMap(o => {
                            const base = [
                                o.order_number,
                                o.reference_number || '',
                                dayjs(o.order_date).format('YYYY-MM-DD'),
                                o.customer?.name || o.customer_name || '',
                                o.branch?.name || '',
                            ];
                            const tail = [
                                o.total_amount,
                                o.status,
                                o.payment_status || 'UNPAID',
                                o.created_at ? dayjs(o.created_at).format('YYYY-MM-DD HH:mm') : '',
                                o.creator?.name || '',
                                o.confirmed_at ? dayjs(o.confirmed_at).format('YYYY-MM-DD HH:mm') : '',
                                o.confirmer?.name || '',
                            ];
                            const items = o.items || [];
                            if (!items.length) {
                                // Order with no line items — still emit one row with blank item cols
                                return [[...base, '', '', '', '', '', ...tail]];
                            }
                            return items.map(it => [
                                ...base,
                                it.item?.name || '',
                                Number(it.quantity),
                                it.item?.unit?.name || it.item?.unit?.abbreviation || '',
                                Number(it.unit_price),
                                Number(it.line_total),
                                ...tail,
                            ]);
                        })}
                        style={{ borderRadius: 8 }}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openCreateModal}
                        style={{ height: 45, borderRadius: 10, fontWeight: 600, padding: '0 24px', width: isMobile ? '100%' : 'auto' }}
                    >
                        New Sales Order
                    </Button>
                </Space>
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        title="Total Sales"
                        value={totalSales}
                        prefix="₱"
                        icon={<i className="fa-solid fa-money-bill-trend-up"></i>}
                        color="#10b981"
                        subText="Confirmed revenue"
                    />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        title="Accounts Receivable"
                        value={totalAr}
                        prefix="₱"
                        icon={<i className="fa-solid fa-hand-holding-dollar"></i>}
                        color="#3b82f6"
                        subText="Total outstanding balance"
                    />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        title="Unpaid Invoices"
                        value={unpaidInvoices}
                        prefix=""
                        icon={<i className="fa-solid fa-receipt" />}
                        color="#f59e0b"
                        subText="Pending collections"
                    />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        title="Draft Orders"
                        value={pendingCount}
                        prefix=""
                        icon={<i className="fa-solid fa-pencil" />}
                        color="#8b5cf6"
                        subText="Waiting for confirmation"
                    />
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                {orders.length > 0 ? (
                    <>
                        <div style={{ padding: 16, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center' }}>
                            <Space wrap>
                                {selectedRowKeys.length > 0 && (
                                    <>
                                        <Typography.Text strong>{selectedRowKeys.length} selected</Typography.Text>
                                        {canConfirmOrders && (
                                            <Button
                                                type="primary"
                                                icon={<CheckCircleOutlined />}
                                                disabled={!confirmableSelected.length}
                                                onClick={openBulkConfirmModal}
                                            >
                                                Confirm Drafts ({confirmableSelected.length})
                                            </Button>
                                        )}
                                        {isManagerOrAdmin && (
                                            <Button
                                                icon={<DollarOutlined />}
                                                disabled={!payableSelected.length}
                                                onClick={openBulkPaymentModal}
                                            >
                                                Record Payments ({payableSelected.length})
                                            </Button>
                                        )}
                                        <Button onClick={() => setSelectedRowKeys([])}>Clear Selection</Button>
                                    </>
                                )}
                            </Space>
                            <Space wrap style={{ width: isMobile ? '100%' : 'auto' }}>
                                <Select
                                    aria-label="Filter by payment status"
                                    value={paymentStatusFilter}
                                    onChange={setPaymentStatusFilter}
                                    style={{ width: isMobile ? '100%' : 180 }}
                                    options={[
                                        { value: 'ALL', label: 'All Payment Statuses' },
                                        { value: 'PAID', label: 'Paid' },
                                        { value: 'UNPAID', label: 'Unpaid' },
                                        { value: 'PARTIAL', label: 'Partial' },
                                    ]}
                                />
                                <Input
                                    placeholder="Search order #, reference #, customer, branch, status..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ width: isMobile ? '100%' : 340, maxWidth: '100%', borderRadius: 10, height: 40 }}
                                />
                            </Space>
                        </div>
                        <Table
                            dataSource={filteredOrders}
                            columns={columns}
                            rowKey="id"
                            loading={loading}
                            size={isMobile ? 'small' : 'middle'}
                            scroll={{ x: isMobile ? 960 : undefined }}
                            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                            rowSelection={(canConfirmOrders || isManagerOrAdmin) ? {
                                selectedRowKeys,
                                preserveSelectedRowKeys: true,
                                onChange: keys => setSelectedRowKeys(keys),
                                getCheckboxProps: record => ({
                                    disabled: !canSelectOrder(record),
                                    name: `Select ${record.order_number}`,
                                }),
                            } : undefined}
                        />
                    </>
                ) : (
                    <Empty description="No sales orders found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </Card>

            <Modal
                title={editingOrder ? 'Edit Sales Order' : 'New Sales Order'}
                open={isModalVisible}
                onCancel={closeSalesOrderModal}
                footer={null}
                width={isMobile ? '100%' : 960}
                style={{ top: isMobile ? 16 : 24 }}
                bodyStyle={{ padding: isMobile ? 12 : 24 }}
            >
                <Form form={form} layout="vertical" onFinish={handleSaveSalesOrder}>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
                        <Form.Item name="order_number" label="Order Number" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="order_date" label="Order Date" rules={[{ required: true }]}>
                            <DatePicker style={{ width: '100%' }} showTime />
                        </Form.Item>
                    </div>

                    <Form.Item name="ar_number" label="Acknowledgement Receipt #">
                        <Input placeholder="e.g., AR-001 (optional)" />
                    </Form.Item>

                    <Form.Item name="reference_number" label="Reference #">
                        <Input placeholder="e.g., DR-001, PO#12345 (optional)" />
                    </Form.Item>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
                        <Form.Item name="partner_id" label="Customer">
                            <Select
                                showSearch
                                allowClear
                                placeholder="Search customer..."
                                optionFilterProp="label"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                dropdownRender={(menu) => (
                                    <>
                                        {menu}
                                        <Divider style={{ margin: '4px 0' }} />
                                        <div style={{ padding: '4px 8px' }}>
                                            <Button
                                                type="link"
                                                icon={<PlusOutlined />}
                                                onClick={() => setIsAddCustomerModalVisible(true)}
                                            >
                                                Add New Customer
                                            </Button>
                                        </div>
                                    </>
                                )}
                                options={customers.map(c => ({ label: c.name, value: c.id }))}
                            />
                        </Form.Item>
                        <Form.Item name="branch_id" label="Branch" rules={[{ required: true }]}>
                            <Select>
                                {branches.map(b => (
                                    <Option key={b.id} value={b.id}>{b.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    <Form.Item name="item_category_id" label="Item Category">
                        <Select
                            allowClear
                            placeholder="All categories"
                            value={selectedItemCategoryId}
                            onChange={(value) => setSelectedItemCategoryId(value || null)}
                        >
                            {categories.map((category) => (
                                <Option key={category.id} value={category.id}>
                                    {category.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Divider>Items</Divider>
                    <Form.List name="items">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <div
                                        key={key}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(180px, 1fr))' : '200px 240px 120px 120px 90px',
                                            alignItems: 'center',
                                            gap: 8,
                                            marginBottom: 12,
                                        }}
                                    >
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'item_id']}
                                            rules={[{ required: true, message: 'Missing item' }]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Select
                                                style={{ width: '100%' }}
                                                placeholder="Select Item"
                                                onChange={(itemId) => {
                                                    const selectedItem = items.find((item) => item.id === itemId);
                                                    if (!selectedItem) {
                                                        return;
                                                    }
                                                    form.setFields([
                                                        {
                                                            name: ['items', name, 'unit_price'],
                                                            value: selectedItem.base_price ?? 0,
                                                        },
                                                        {
                                                            name: ['items', name, 'palay_lot_id'],
                                                            value: null,
                                                        },
                                                    ]);
                                                }}
                                            >
                                                {(selectedItemCategoryId
                                                    ? items.filter((item) => item.category?.id === selectedItemCategoryId)
                                                    : items
                                                ).map((item) => (
                                                    <Option key={item.id} value={item.id}>
                                                        {item.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(prev, cur) => (
                                                prev?.branch_id !== cur?.branch_id
                                                || prev?.items?.[name]?.item_id !== cur?.items?.[name]?.item_id
                                                || prev?.items?.[name]?.palay_lot_id !== cur?.items?.[name]?.palay_lot_id
                                            )}
                                        >
                                            {({ getFieldValue }) => {
                                                const itemId = getFieldValue(['items', name, 'item_id']);
                                                const branchId = getFieldValue('branch_id');
                                                if (!itemId || !isPalayItem(itemId)) {
                                                    return null;
                                                }
                                                const availableLots = palayLots.filter((lot) => (
                                                    lot.item_id === itemId
                                                    && (!branchId || lot.branch_id === branchId)
                                                ));
                                                return (
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'palay_lot_id']}
                                                        rules={[{ required: true, message: 'Select lot' }]}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        <Select style={{ width: '100%' }}>
                                                            {availableLots.map((lot) => (
                                                                <Option key={lot.id} value={lot.id}>
                                                                    {lot.lot_number}
                                                                    {lot.storage_code ? ` (${lot.storage_code})` : ''}
                                                                    {` - ${lot.purchase?.partner?.name || 'No supplier'}`}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                );
                                            }}
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'quantity']}
                                            rules={[
                                                { required: true, message: 'Missing qty' },
                                                ({ getFieldValue }) => ({
                                                    validator(_, value) {
                                                        const itemId = getFieldValue(['items', name, 'item_id']);
                                                        if (!isPalayItem(itemId)) return Promise.resolve();
                                                        const lotId = getFieldValue(['items', name, 'palay_lot_id']);
                                                        const lot = palayLots.find(l => l.id === lotId);
                                                        if (!lot) return Promise.reject(new Error('Select lot first'));
                                                        const remaining = Number(lot.current_quantity || 0);
                                                        if (value && Number(value) > remaining) {
                                                            return Promise.reject(new Error(`Qty exceeds remaining ${remaining.toLocaleString()} kg`));
                                                        }
                                                        return Promise.resolve();
                                                    }
                                                })
                                            ]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <InputNumber placeholder="Qty" min={0.01} style={{ width: '100%' }} />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'unit_price']}
                                            rules={[{ required: true, message: 'Missing price' }]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <InputNumber placeholder="Price" min={0} precision={2} style={{ width: '100%' }} />
                                        </Form.Item>
                                        <Button
                                            onClick={() => remove(name)}
                                            type="dashed"
                                            danger
                                            style={{ height: 40, borderRadius: 8, width: isMobile ? '100%' : 'auto' }}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                ))}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                        Add Item
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <Form.Item name="discount" label="Discount">
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item name="remarks" label="Remarks">
                        <Input.TextArea rows={2} placeholder="Additional notes (optional)" />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>
                            {editingOrder ? 'Save Changes' : 'Create Sales Order'}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Add New Customer"
                open={isAddCustomerModalVisible}
                onCancel={() => {
                    setIsAddCustomerModalVisible(false);
                    addCustomerForm.resetFields();
                }}
                onOk={() => addCustomerForm.submit()}
                okText="Save Customer"
                width={isMobile ? '100%' : 500}
                style={{ top: isMobile ? 16 : 24 }}
            >
                <Form form={addCustomerForm} layout="vertical" onFinish={handleAddCustomer}>
                    <Form.Item
                        name="last_name"
                        label="Last Name"
                        rules={[
                            { required: true, whitespace: true, message: 'Please enter the last name' },
                            { max: 120, message: 'Last name must not exceed 120 characters' },
                        ]}
                    >
                        <Input placeholder="e.g., Dela Cruz" />
                    </Form.Item>
                    <Form.Item
                        name="first_name"
                        label="First Name"
                        rules={[
                            { required: true, whitespace: true, message: 'Please enter the first name' },
                            { max: 120, message: 'First name must not exceed 120 characters' },
                        ]}
                    >
                        <Input placeholder="e.g., Juan" />
                    </Form.Item>
                    <Form.Item
                        name="phone"
                        label="Phone Number"
                    >
                        <Input placeholder="e.g., 09171234567" />
                    </Form.Item>
                    <Form.Item
                        name="address"
                        label="Address"
                    >
                        <Input.TextArea placeholder="Customer address" rows={2} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`Confirm ${confirmableSelected.length} Sales Order${confirmableSelected.length === 1 ? '' : 's'}`}
                open={bulkConfirmOpen}
                onCancel={() => !bulkConfirming && setBulkConfirmOpen(false)}
                onOk={handleBulkConfirm}
                okText={`Confirm ${confirmableSelected.length} Order${confirmableSelected.length === 1 ? '' : 's'}`}
                confirmLoading={bulkConfirming}
                closable={!bulkConfirming}
                maskClosable={!bulkConfirming}
                width={isMobile ? '100%' : 760}
            >
                <Typography.Paragraph type="secondary">
                    Confirmation creates invoices and posts inventory and accounting entries for every listed order.
                    If any order fails, none will be confirmed.
                </Typography.Paragraph>
                <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={confirmableSelected}
                    scroll={{ x: 620, y: 320 }}
                    columns={[
                        { title: 'Order #', dataIndex: 'order_number', key: 'order_number' },
                        { title: 'Customer', key: 'customer', render: (_, row) => row.customer?.name || row.customer_name || 'Walk-in' },
                        { title: 'Branch', key: 'branch', render: (_, row) => row.branch?.name || '—' },
                        { title: 'Amount', dataIndex: 'total_amount', key: 'total_amount', align: 'right', render: value => `₱${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
                    ]}
                    summary={() => (
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={3}><Typography.Text strong>Total</Typography.Text></Table.Summary.Cell>
                            <Table.Summary.Cell index={3} align="right">
                                <Typography.Text strong>
                                    ₱{confirmableSelected.reduce((sum, order) => sum + Number(order.total_amount || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </Typography.Text>
                            </Table.Summary.Cell>
                        </Table.Summary.Row>
                    )}
                />
            </Modal>

            <Modal
                title={`Record ${bulkPaymentRows.length} Payment${bulkPaymentRows.length === 1 ? '' : 's'}`}
                open={bulkPaymentOpen}
                onCancel={() => {
                    if (!bulkPaying) {
                        setBulkPaymentOpen(false);
                        setBulkPaymentRows([]);
                    }
                }}
                footer={[
                    <Button key="cancel" disabled={bulkPaying} onClick={() => {
                        setBulkPaymentOpen(false);
                        setBulkPaymentRows([]);
                    }}>
                        Cancel
                    </Button>,
                    <Button key="submit" type="primary" loading={bulkPaying} disabled={!bulkPaymentRows.length} onClick={handleBulkPayment}>
                        Record {bulkPaymentRows.length} Payment{bulkPaymentRows.length === 1 ? '' : 's'}
                    </Button>,
                ]}
                closable={!bulkPaying}
                maskClosable={!bulkPaying}
                width={isMobile ? '100%' : 980}
                style={{ top: isMobile ? 8 : 24 }}
            >
                <Typography.Paragraph type="secondary">
                    Enter a full or partial amount for each invoice. The shared details below apply to every payment,
                    and nothing is saved if any payment fails.
                </Typography.Paragraph>
                <Form form={bulkPaymentForm} layout="vertical">
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item name="payment_date" label="Payment Date" rules={[{ required: true, message: 'Payment date is required' }]}>
                                <DatePicker style={{ width: '100%' }} showTime />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="payment_method" label="Method" rules={[{ required: true, message: 'Payment method is required' }]}>
                                <Select>
                                    <Option value="CASH">Cash</Option>
                                    <Option value="CHECK">Check</Option>
                                    <Option value="BANK_TRANSFER">Bank Transfer</Option>
                                    <Option value="EWALLET">E-Wallet</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="reference_number" label="Shared Reference #" rules={[{ max: 255 }]}>
                                <Input placeholder="Optional receipt or deposit reference" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
                <Table
                    rowKey="invoice_id"
                    size="small"
                    pagination={false}
                    dataSource={bulkPaymentRows}
                    scroll={{ x: 800, y: 340 }}
                    columns={[
                        { title: 'Order #', dataIndex: 'order_number', key: 'order_number' },
                        { title: 'Customer', dataIndex: 'customer', key: 'customer' },
                        { title: 'Invoice #', dataIndex: 'invoice_number', key: 'invoice_number' },
                        { title: 'Outstanding', dataIndex: 'outstanding', key: 'outstanding', align: 'right', render: value => `₱${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
                        {
                            title: 'Payment Amount', key: 'amount', width: 180,
                            render: (_, row) => <InputNumber
                                min={0.01}
                                max={row.outstanding}
                                precision={2}
                                prefix="₱"
                                value={row.amount}
                                status={!Number(row.amount) || Number(row.amount) > row.outstanding ? 'error' : ''}
                                onChange={value => updateBulkPaymentAmount(row.invoice_id, value)}
                                style={{ width: '100%' }}
                            />,
                        },
                        {
                            title: '', key: 'remove', width: 48,
                            render: (_, row) => <Tooltip title="Remove from payment list"><Button type="text" danger icon={<CloseOutlined />} onClick={() => removeBulkPaymentRow(row.invoice_id)} /></Tooltip>,
                        },
                    ]}
                />
                <Row gutter={[16, 8]} justify="end" style={{ marginTop: 16 }}>
                    <Col><Typography.Text type="secondary">Outstanding: </Typography.Text><Typography.Text strong>₱{bulkOutstandingTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography.Text></Col>
                    <Col><Typography.Text type="secondary">Paying now: </Typography.Text><Typography.Text strong>₱{bulkPaymentTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography.Text></Col>
                    <Col><Typography.Text type="secondary">Remaining: </Typography.Text><Typography.Text strong>₱{Math.max(0, bulkOutstandingTotal - bulkPaymentTotal).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography.Text></Col>
                </Row>
            </Modal>

            <Modal
                title="Record Payment"
                open={isPaymentModalVisible}
                onCancel={() => setIsPaymentModalVisible(false)}
                footer={null}
                width={isMobile ? '100%' : undefined}
                style={{ top: isMobile ? 16 : 24 }}
                bodyStyle={{ padding: isMobile ? 12 : 24 }}
            >
                <Form form={paymentForm} layout="vertical" onFinish={handlePayment}>
                    <Form.Item label="Invoice Number" name="invoice_number">
                        <Input disabled />
                    </Form.Item>
                    <Form.Item name="payment_number" label="Payment #" initialValue={`PAY-${dayjs().valueOf()}`} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="payment_date" label="Payment Date" initialValue={dayjs()} rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} showTime />
                    </Form.Item>
                    <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} min={0.01} />
                    </Form.Item>
                    <Form.Item name="payment_method" label="Method" initialValue="CASH" rules={[{ required: true }]}>
                        <Select>
                            <Option value="CASH">Cash</Option>
                            <Option value="CHECK">Check</Option>
                            <Option value="BANK_TRANSFER">Bank Transfer</Option>
                            <Option value="EWALLET">E-Wallet</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="reference_number" label="Reference #">
                        <Input />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>
                            Submit Payment
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <SalesOrderPrintModal
                visible={isPrintModalVisible}
                onCancel={() => setIsPrintModalVisible(false)}
                data={selectedOrder}
            />

            <Modal
                title="Sales Order Details"
                open={isViewModalVisible}
                onCancel={() => {
                    setIsViewModalVisible(false);
                    setViewOrder(null);
                }}
                footer={null}
                width={isMobile ? 'calc(100vw - 16px)' : 1100}
                style={{ top: isMobile ? 8 : 24 }}
                styles={{
                    body: {
                        padding: isMobile ? 12 : 24,
                        maxHeight: isMobile ? 'calc(100vh - 72px)' : 'calc(100vh - 104px)',
                        overflowY: 'auto',
                    },
                }}
            >
                {viewLoading ? (
                    <Typography.Text>Loading...</Typography.Text>
                ) : viewOrder ? (
                    <>
                        <Descriptions bordered size="small" column={isMobile ? 1 : 2} style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="Order #">{viewOrder.order_number}</Descriptions.Item>
                            <Descriptions.Item label="Reference #">{viewOrder.reference_number || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={viewOrder.status === 'COMPLETED' ? 'green' : 'blue'}>{viewOrder.status}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Order Date">{dayjs(viewOrder.order_date).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
                            <Descriptions.Item label="Branch">{viewOrder.branch?.name || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Customer">{viewOrder.customer?.name || viewOrder.customer_name || 'Walk-in'}</Descriptions.Item>
                            <Descriptions.Item label="Payment">
                                <Tag color={viewOrder.payment_status === 'PAID' ? 'green' : viewOrder.payment_status === 'PARTIAL' ? 'orange' : 'red'}>
                                    {viewOrder.payment_status || 'UNPAID'}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="AR #">{viewOrder.ar_number || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Encoded At">
                                {viewOrder.created_at
                                    ? `${dayjs(viewOrder.created_at).format('YYYY-MM-DD HH:mm')}${viewOrder.creator?.name ? ` by ${viewOrder.creator.name}` : ''}`
                                    : '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Confirmed At">
                                {viewOrder.confirmed_at
                                    ? `${dayjs(viewOrder.confirmed_at).format('YYYY-MM-DD HH:mm')}${viewOrder.confirmer?.name ? ` by ${viewOrder.confirmer.name}` : ''}`
                                    : '—'}
                            </Descriptions.Item>
                            {viewOrder.remarks && (
                                <Descriptions.Item label="Remarks" span={2}>{viewOrder.remarks}</Descriptions.Item>
                            )}
                        </Descriptions>

                        <Table
                            dataSource={displayedOrderItems}
                            rowKey="id"
                            size="small"
                            pagination={false}
                            scroll={{ x: 760 }}
                            columns={[
                                { title: 'Item', dataIndex: ['item', 'name'], key: 'item' },
                                { title: 'Category', dataIndex: ['item', 'category', 'name'], key: 'category' },
                                { title: 'Lot', dataIndex: ['lot', 'lot_number'], key: 'lot' },
                                { title: 'Unit', dataIndex: ['item', 'unit', 'name'], key: 'unit' },
                                { title: 'Qty', dataIndex: 'quantity', key: 'qty', align: 'right', render: (val) => Number(val).toLocaleString() },
                                { title: 'Unit Price', dataIndex: 'unit_price', key: 'unit_price', align: 'right', render: (val) => `₱${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
                                { title: 'Line Total', dataIndex: 'line_total', key: 'line_total', align: 'right', render: (val) => `₱${Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
                            ]}
                            summary={() => {
                                const items = displayedOrderItems;
                                const subtotal = items.reduce((sum, item) => sum + Number(item.line_total || 0), 0);
                                const discount = Number(viewOrder.discount || 0);
                                const total = Number(viewOrder.total_amount || subtotal - discount);

                                return (
                                    <>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={6} align="right">Subtotal</Table.Summary.Cell>
                                            <Table.Summary.Cell index={6} align="right">₱{subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Table.Summary.Cell>
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={6} align="right">Discount</Table.Summary.Cell>
                                            <Table.Summary.Cell index={6} align="right">₱{discount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Table.Summary.Cell>
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={6} align="right"><strong>Total</strong></Table.Summary.Cell>
                                            <Table.Summary.Cell index={6} align="right"><strong>₱{total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </>
                                );
                            }}
                        />

                        {viewOrder.rice_allowance_batch && (
                            <>
                                <Divider style={{ marginTop: 24 }}>Rice Allowance Employees</Divider>
                                <Descriptions bordered size="small" column={isMobile ? 1 : 2} style={{ marginBottom: 16 }}>
                                    <Descriptions.Item label="Allowance Batch">{viewOrder.rice_allowance_batch.batch_number}</Descriptions.Item>
                                    <Descriptions.Item label="Employees">{viewOrder.rice_allowance_batch.total_employees}</Descriptions.Item>
                                </Descriptions>
                                <Table
                                    dataSource={viewOrder.rice_allowance_batch.recipients || []}
                                    rowKey="id"
                                    size="small"
                                    sticky
                                    pagination={{
                                        defaultPageSize: 10,
                                        pageSizeOptions: [10, 25, 50],
                                        showSizeChanger: true,
                                        hideOnSinglePage: true,
                                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} employees`,
                                    }}
                                    scroll={{ x: 920, y: 360 }}
                                    columns={[
                                        { title: 'Employee', width: 270, fixed: isMobile ? undefined : 'left', render: (_, row) => <div><Typography.Text strong>{row.employee?.name || 'Unknown'}</Typography.Text><br /><Typography.Text type="secondary">{row.employee?.code || 'N/A'}</Typography.Text></div> },
                                        { title: 'Rice Item', width: 150, render: (_, row) => row.item?.name || 'N/A' },
                                        { title: 'KG', dataIndex: 'allowance_kg', width: 80, align: 'right', render: value => Number(value).toLocaleString() },
                                        { title: 'Price', dataIndex: 'unit_price', width: 120, align: 'right', render: value => `₱${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
                                        { title: 'Amount', dataIndex: 'line_total', width: 130, align: 'right', render: value => `₱${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
                                        { title: 'Override Reason', dataIndex: 'override_reason', width: 170, ellipsis: true, render: value => value || '—' },
                                    ]}
                                />
                            </>
                        )}

                        {viewOrder.invoice && (
                            <>
                                <Divider style={{ marginTop: 24 }}>Payment Summary</Divider>
                                <Descriptions bordered size="small" column={isMobile ? 1 : 3} style={{ marginBottom: 16 }}>
                                    <Descriptions.Item label="Invoice #">{viewOrder.invoice.invoice_number}</Descriptions.Item>
                                    <Descriptions.Item label="Total Amount">
                                        <strong>₱{Number(viewOrder.invoice.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Paid Amount">
                                        <span style={{ color: '#10b981', fontWeight: 600 }}>
                                            ₱{Number(viewOrder.invoice.paid_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Balance Due" span={3}>
                                        <span style={{ color: Number(viewOrder.invoice.balance) > 0 ? '#ef4444' : '#10b981', fontWeight: 700, fontSize: 16 }}>
                                            ₱{Number(viewOrder.invoice.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </span>
                                    </Descriptions.Item>
                                </Descriptions>

                                {viewOrder.invoice.payments && viewOrder.invoice.payments.length > 0 && (
                                    <>
                                        <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Payment History</Typography.Text>
                                        <Table
                                            dataSource={viewOrder.invoice.payments}
                                            rowKey="id"
                                            size="small"
                                            pagination={false}
                                            columns={[
                                                { title: 'Payment #', dataIndex: 'payment_number', key: 'payment_number' },
                                                { title: 'Date', dataIndex: 'payment_date', key: 'payment_date', render: (val) => dayjs(val).format('YYYY-MM-DD HH:mm') },
                                                { title: 'Method', dataIndex: 'payment_method', key: 'payment_method' },
                                                { title: 'Reference #', dataIndex: 'reference_number', key: 'reference_number', render: (val) => val || '—' },
                                                { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right', render: (val) => <span style={{ color: '#10b981', fontWeight: 600 }}>₱{Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> },
                                            ]}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </>
                ) : null}
            </Modal>
        </div>
    );
};

export default SalesPage;
